import styled from 'styled-components';

export const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-width: 0;
    width: 100%;
`;

export const Title = styled.h1`
    margin: 0;
    font-size: clamp(1.125rem, 3.5vw, 1.5rem);
    color: ${({ theme }) => theme.colors.gray[900]};
`;

export const Subtitle = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.gray[700]};
`;

export const PeriodLimit = styled(Subtitle)`
    margin-top: 8px;
`;

export const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 260px), 1fr));
    gap: clamp(16px, 3vw, 24px);
`;

export const ReportCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    gap: 12px;
    width: 100%;
    padding: clamp(20px, 4vw, 28px);
    background: #fff;
    border: 1px solid ${({ theme }) => theme.colors.gray[200]};
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
    font: inherit;
    color: inherit;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        border-color: ${({ theme }) => theme.colors.primary.main};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.primary.main};
        outline-offset: 2px;
    }
`;

export const CardIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.colors.primary.light}20;
    color: ${({ theme }) => theme.colors.primary.main};

    svg {
        width: 24px;
        height: 24px;
    }
`;

export const CardTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.gray[900]};
`;

export const CardDescription = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.gray[700]};
`;

export const Overlay = styled.div`
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    padding: 16px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
`;

export const ModalBox = styled.div`
    box-sizing: border-box;
    background: white;
    border-radius: 12px;
    width: 100%;
    max-width: min(480px, calc(100vw - 32px));
    max-height: min(90vh, 90dvh);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

export const ModalHeader = styled.div`
    flex-shrink: 0;
    padding: 24px 24px 0;
`;

export const ModalTitle = styled.h2`
    margin: 0 0 8px;
    font-size: 18px;
    color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
`;

export const ModalDescription = styled.p`
    margin: 0 0 8px;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme?.colors?.gray?.[700] || '#4B5563'};
`;

export const ModalForm = styled.form`
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1 1 auto;
`;

export const ModalBody = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 24px 8px;
`;

export const ModalFooter = styled.div`
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 24px 24px;
    border-top: 1px solid ${({ theme }) => theme?.colors?.gray?.[200] || '#e5e7eb'};
    background: white;
`;

export const FilterField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;

    label {
        font-size: 13px;
        font-weight: 500;
        color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};
    }

    input,
    select {
        width: 100%;
        min-width: 0;
        box-sizing: border-box;
        padding: 10px 14px;
        border: 1px solid ${({ theme, $hasError }) =>
            $hasError
                ? theme?.colors?.danger?.main || '#FC5050'
                : theme?.colors?.gray?.[200] || '#BCBCBC'};
        border-radius: 8px;
        font: inherit;
        font-size: 14px;
        background: #fff;

        &:focus {
            border-color: ${({ theme, $hasError }) =>
                $hasError
                    ? theme?.colors?.danger?.main || '#FC5050'
                    : theme?.colors?.primary?.main || '#3B5998'};
            outline: none;
        }
    }
`;

export const FieldHint = styled.span`
    font-size: 12px;
    line-height: 1.4;
    color: ${({ theme, $error }) =>
        $error
            ? theme?.colors?.danger?.main || '#FC5050'
            : theme?.colors?.gray?.[500] || '#6B7280'};
`;

export const RadioGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export const RadioOption = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border: 1px solid ${({ theme, $selected }) =>
        $selected
            ? theme?.colors?.primary?.main || '#3B5998'
            : theme?.colors?.gray?.[200] || '#e5e5e5'};
    border-radius: 8px;
    cursor: pointer;
    background: ${({ $selected }) => ($selected ? 'rgba(59, 89, 152, 0.06)' : '#fff')};
    font-size: 14px;
    color: ${({ theme }) => theme?.colors?.gray?.[900] || '#222'};

    input {
        margin: 0;
        width: auto;
        padding: 0;
        accent-color: ${({ theme }) => theme?.colors?.primary?.main || '#3B5998'};
    }
`;

export const FormError = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${({ theme }) => theme?.colors?.danger?.main || '#FC5050'};
`;
